import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IdCard as IdCardIcon, Briefcase, User, FileText, CalendarDays, GraduationCap, Inbox, Award, Wallet, ClipboardList, UserPlus } from 'lucide-react';
import { employeeApi } from '../../api/hr';
import { PageHeader } from '../../components/PageHeader';

/** All staff sections now shown as icon tiles here, per feedback, rather than only 3 of them plus stale "coming soon" placeholder cards for modules that are actually built and live (Leave, Training, HR Requests, Performance). */
export function StaffDashboardPage() {
  const profileQuery = useQuery({ queryKey: ['my-profile'], queryFn: () => employeeApi.getMe() });
  const employee = profileQuery.data?.data;

  return (
    <div className="page">
      <PageHeader title={`Welcome, ${employee?.preferred_name ?? employee?.full_name ?? '…'}`} description="Here's your workspace overview." />

      {employee && (
        <section className="card">
          <div className="project-header-grid">
            <HeaderField label="Staff ID" value={employee.staff_id ?? 'Not yet assigned'} />
            <HeaderField label="Position" value={employee.position_name ?? '-'} />
            <HeaderField label="Department" value={employee.department_name ?? '-'} />
            <HeaderField label="Duty Station" value={employee.duty_station_name ?? '-'} />
            <HeaderField label="Employment Status" value={employee.employment_status} />
            <HeaderField label="Supervisor" value={employee.supervisor_name ?? '-'} />
          </div>
        </section>
      )}

      <div className="quick-actions-grid">
        <Link className="quick-action" to="/staff/profile">
          <span className="quick-action-icon"><User size={18} /></span>
          <div><strong>My Profile</strong><div className="muted">View and update personal info</div></div>
        </Link>
        <Link className="quick-action" to="/staff/id-card">
          <span className="quick-action-icon"><IdCardIcon size={18} /></span>
          <div><strong>My ID Card</strong><div className="muted">View your issued card</div></div>
        </Link>
        <Link className="quick-action" to="/staff/leave">
          <span className="quick-action-icon"><CalendarDays size={18} /></span>
          <div><strong>My Leave</strong><div className="muted">Apply and check balances</div></div>
        </Link>
        <Link className="quick-action" to="/staff/requests">
          <span className="quick-action-icon"><Inbox size={18} /></span>
          <div><strong>My Requests</strong><div className="muted">Submit an HR request</div></div>
        </Link>
        <Link className="quick-action" to="/staff/training">
          <span className="quick-action-icon"><GraduationCap size={18} /></span>
          <div><strong>My Training</strong><div className="muted">Request or review training</div></div>
        </Link>
        <Link className="quick-action" to="/staff/performance">
          <span className="quick-action-icon"><Award size={18} /></span>
          <div><strong>My Performance</strong><div className="muted">View appraisals</div></div>
        </Link>
        <Link className="quick-action" to="/staff/payroll">
          <span className="quick-action-icon"><Wallet size={18} /></span>
          <div><strong>My Payroll</strong><div className="muted">Timesheets and payslips</div></div>
        </Link>
        <Link className="quick-action" to="/staff/onboarding">
          <span className="quick-action-icon"><ClipboardList size={18} /></span>
          <div><strong>My Onboarding</strong><div className="muted">Track your onboarding progress</div></div>
        </Link>
        <Link className="quick-action" to="/staff/probation">
          <span className="quick-action-icon"><Award size={18} /></span>
          <div><strong>My Probation</strong><div className="muted">Status and review history</div></div>
        </Link>
        <Link className="quick-action" to="/staff/movement">
          <span className="quick-action-icon"><Briefcase size={18} /></span>
          <div><strong>My Staff Movement</strong><div className="muted">Request or track a transfer</div></div>
        </Link>
        <Link className="quick-action" to="/staff/restricted-cases">
          <span className="quick-action-icon"><FileText size={18} /></span>
          <div><strong>Report a Concern</strong><div className="muted">Confidential grievance and safeguarding reporting</div></div>
        </Link>
        <Link className="quick-action" to="/staff/separation">
          <span className="quick-action-icon"><UserPlus size={18} /></span>
          <div><strong>My Separation</strong><div className="muted">Status of any separation case on file</div></div>
        </Link>
        <Link className="quick-action" to="/staff/directory">
          <span className="quick-action-icon"><Briefcase size={18} /></span>
          <div><strong>Staff Directory</strong><div className="muted">Find a colleague</div></div>
        </Link>
      </div>

      <div className="dashboard-chart-row">
        <section className="card placeholder-card">
          <h2><FileText size={16} className="inline-icon" /> My Contract</h2>
          <p className="muted">Contracts are managed by HR - visit HR Staff Management for contract details.</p>
        </section>
      </div>
    </div>
  );
}

function HeaderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="project-header-field">
      <div className="project-header-field-label">{label}</div>
      <div className="project-header-field-value">{value}</div>
    </div>
  );
}
